import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Tooltip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Block as BlockIcon,
  CheckCircle as ActiveIcon,
  AccountBalance as PortfolioIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend } from 'recharts';
import { beneficiariesApi } from '../services/api';
import { formatCurrency } from '../utils/formatters';
import { useApp } from '../App';

const COLORS = ['#1B365D', '#C5A572', '#2E5090', '#D4BC94', '#0F1F38', '#A68B5B'];

const Beneficiaries = () => {
  const { showNotification } = useApp();
  const [loading, setLoading] = useState(true);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editBeneficiary, setEditBeneficiary] = useState(null);
  const [detailBeneficiary, setDetailBeneficiary] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await beneficiariesApi.getAll();
      setBeneficiaries(response.data);
    } catch (error) {
      showNotification('Ошибка загрузки бенефициаров', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (beneficiary = null) => {
    setEditBeneficiary(
      beneficiary || {
        code: '',
        name: '',
        fullName: '',
        email: '',
        phone: '',
        notes: '',
      }
    );
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editBeneficiary.id) {
        await beneficiariesApi.update(editBeneficiary.id, editBeneficiary);
        showNotification('Бенефициар обновлен', 'success');
      } else {
        await beneficiariesApi.create(editBeneficiary);
        showNotification('Бенефициар создан', 'success');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      showNotification(error.response?.data?.error || 'Ошибка сохранения', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить бенефициара?')) return;

    try {
      await beneficiariesApi.delete(id);
      showNotification('Бенефициар удален', 'success');
      fetchData();
    } catch (error) {
      showNotification(error.response?.data?.error || 'Ошибка удаления', 'error');
    }
  };

  const handleToggleActive = async (beneficiary) => {
    try {
      if (beneficiary.isActive) {
        await beneficiariesApi.deactivate(beneficiary.id);
        showNotification('Бенефициар деактивирован', 'success');
      } else {
        await beneficiariesApi.activate(beneficiary.id);
        showNotification('Бенефициар активирован', 'success');
      }
      fetchData();
    } catch (error) {
      showNotification('Ошибка изменения статуса', 'error');
    }
  };

  const handleViewDetails = async (id) => {
    try {
      const response = await beneficiariesApi.getById(id);
      setDetailBeneficiary(response.data);
    } catch (error) {
      showNotification('Ошибка загрузки деталей', 'error');
    }
  };

  const getJurisdictionChartData = () => {
    if (!detailBeneficiary?.stats?.byJurisdiction) return [];
    return Object.entries(detailBeneficiary.stats.byJurisdiction).map(([name, data]) => ({
      name,
      value: data.value,
      count: data.count,
    }));
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>
          Бенефициары
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Добавить бенефициара
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Beneficiaries List */}
        <Grid item xs={12} md={detailBeneficiary ? 7 : 12}>
          <Card>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Код</TableCell>
                    <TableCell>Имя</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Структуры</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell width={150}>Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        Загрузка...
                      </TableCell>
                    </TableRow>
                  ) : beneficiaries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        Нет бенефициаров
                      </TableCell>
                    </TableRow>
                  ) : (
                    beneficiaries.map((beneficiary) => (
                      <TableRow
                        key={beneficiary.id}
                        hover
                        onClick={() => handleViewDetails(beneficiary.id)}
                        sx={{ cursor: 'pointer' }}
                        selected={detailBeneficiary?.id === beneficiary.id}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                              <PersonIcon fontSize="small" />
                            </Avatar>
                            <Typography fontWeight={600}>{beneficiary.code}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{beneficiary.name}</TableCell>
                        <TableCell>{beneficiary.email || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={`${beneficiary.Holders?.length || 0} структур`}
                            size="small"
                            color="info"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={beneficiary.isActive ? <ActiveIcon /> : <BlockIcon />}
                            label={beneficiary.isActive ? 'Активен' : 'Неактивен'}
                            size="small"
                            color={beneficiary.isActive ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Редактировать">
                            <IconButton size="small" onClick={() => handleOpenDialog(beneficiary)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={beneficiary.isActive ? 'Деактивировать' : 'Активировать'}>
                            <IconButton size="small" onClick={() => handleToggleActive(beneficiary)}>
                              {beneficiary.isActive ? <BlockIcon fontSize="small" /> : <ActiveIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Удалить">
                            <IconButton size="small" color="error" onClick={() => handleDelete(beneficiary.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>

        {/* Detail Panel */}
        {detailBeneficiary && (
          <Grid item xs={12} md={5}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <PersonIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">{detailBeneficiary.code}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {detailBeneficiary.name}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton size="small" onClick={() => setDetailBeneficiary(null)}>
                    ×
                  </IconButton>
                </Box>

                {detailBeneficiary.fullName && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {detailBeneficiary.fullName}
                  </Typography>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Statistics */}
                <Typography variant="subtitle2" gutterBottom>
                  Портфель
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Paper sx={{ p: 1.5, bgcolor: 'primary.main', color: 'white' }}>
                      <Typography variant="caption">Всего активов</Typography>
                      <Typography variant="h6">
                        {formatCurrency(detailBeneficiary.stats?.totalValue || 0, 'USD')}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper sx={{ p: 1.5, bgcolor: 'secondary.main', color: 'primary.main' }}>
                      <Typography variant="caption">Структур</Typography>
                      <Typography variant="h6">
                        {detailBeneficiary.stats?.holderCount || 0}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Jurisdiction Chart */}
                {getJurisdictionChartData().length > 0 && (
                  <>
                    <Typography variant="subtitle2" gutterBottom>
                      Распределение по юрисдикциям
                    </Typography>
                    <Box sx={{ height: 200 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={getJurisdictionChartData()}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={70}
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          >
                            {getJurisdictionChartData().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <RechartsTooltip
                            formatter={(value) => formatCurrency(value, 'USD')}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </>
                )}

                {/* Structures List */}
                {detailBeneficiary.Holders?.length > 0 && (
                  <>
                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                      Структуры
                    </Typography>
                    <List dense>
                      {detailBeneficiary.Holders.map((holder) => (
                        <ListItem key={holder.id} sx={{ px: 0 }}>
                          <ListItemText
                            primary={holder.code}
                            secondary={holder.name}
                          />
                          <Chip
                            label={holder.type === 'INDIVIDUAL' ? 'Физ. лицо' : holder.type === 'COMPANY' ? 'Компания' : 'Траст'}
                            size="small"
                            variant="outlined"
                          />
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}

                {detailBeneficiary.notes && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>
                      Заметки
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {detailBeneficiary.notes}
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editBeneficiary?.id ? 'Редактировать бенефициара' : 'Добавить бенефициара'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                label="Код"
                value={editBeneficiary?.code || ''}
                onChange={(e) => setEditBeneficiary({ ...editBeneficiary, code: e.target.value.toUpperCase() })}
                inputProps={{ maxLength: 10 }}
                helperText="Уникальный идентификатор"
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                required
                label="Имя"
                value={editBeneficiary?.name || ''}
                onChange={(e) => setEditBeneficiary({ ...editBeneficiary, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Полное имя"
                value={editBeneficiary?.fullName || ''}
                onChange={(e) => setEditBeneficiary({ ...editBeneficiary, fullName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="email"
                label="Email"
                value={editBeneficiary?.email || ''}
                onChange={(e) => setEditBeneficiary({ ...editBeneficiary, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Телефон"
                value={editBeneficiary?.phone || ''}
                onChange={(e) => setEditBeneficiary({ ...editBeneficiary, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Заметки"
                value={editBeneficiary?.notes || ''}
                onChange={(e) => setEditBeneficiary({ ...editBeneficiary, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={handleSave}>
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Beneficiaries;
